import { useEffect } from "react";
import { useLegendState as useState } from "@/hooks/useLegendState";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  GitCompare, Play, Clock, Webhook, Zap, 
  Brain, HelpCircle, ArrowRight, Plus, X,
  Bot, FileText, RefreshCw, Trash2
} from "lucide-react";
import type { Workflow, WorkflowTrigger, WorkflowNode } from "@/types/aiToolkit";

interface WorkflowBuilderModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workflow?: Workflow | null;
  onSave: (workflow: Partial<Workflow>) => void;
  onDelete?: (workflowId: string) => void;
}

const defaultWorkflow: Partial<Workflow> = {
  name: "",
  description: "",
  tier: "workflow",
  icon: "GitCompare",
  color: "bg-orange-500",
  category: "Automation",
  tags: [],
  access: "free",
  trigger: {
    type: "manual",
    config: {}
  },
  nodes: [],
  edges: []
};

const triggerTypes: { value: WorkflowTrigger["type"]; label: string; icon: typeof Play; description: string }[] = [
  { value: "manual", label: "Manual", icon: Play, description: "Run on demand" },
  { value: "scheduled", label: "Scheduled", icon: Clock, description: "Run on schedule" },
  { value: "webhook", label: "Webhook", icon: Webhook, description: "Triggered by HTTP request" },
  { value: "event", label: "Event", icon: Zap, description: "React to app events" }
];

const nodeTypes: { value: WorkflowNode["type"]; label: string; icon: typeof Brain }[] = [
  { value: "prompt", label: "Prompt", icon: FileText },
  { value: "agent", label: "Agent", icon: Bot },
  { value: "tool", label: "Tool", icon: Bot },
  { value: "knowledge", label: "Knowledge", icon: FileText },
  { value: "parallel", label: "Parallel", icon: RefreshCw },
  { value: "merge", label: "Merge", icon: RefreshCw },
  { value: "validation", label: "Validation", icon: HelpCircle },
  { value: "entity-resolution", label: "Entity Resolution", icon: HelpCircle },
  { value: "confidence-gate", label: "Confidence Gate", icon: HelpCircle },
  { value: "quality-gate", label: "Quality Gate", icon: HelpCircle },
  { value: "condition", label: "Condition", icon: HelpCircle },
  { value: "transform", label: "Transform", icon: RefreshCw },
  { value: "output", label: "Output", icon: ArrowRight }
];

export function WorkflowBuilderModal({
  open,
  onOpenChange,
  workflow,
  onSave,
  onDelete,
}: WorkflowBuilderModalProps) {
  const [formData, setFormData] = useState<Partial<Workflow>>(defaultWorkflow);
  const [tagInput, setTagInput] = useState("");

  useEffect(() => {
    if (workflow) {
      setFormData(workflow);
    } else {
      setFormData({
        ...defaultWorkflow,
        id: `workflow-custom-${Date.now()}`
      });
    }
  }, [workflow, open]);

  const handleSave = () => {
    if (!formData.name?.trim()) return;
    onSave(formData);
    onOpenChange(false);
  };

  const addTag = () => {
    if (tagInput.trim() && !formData.tags?.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...(prev.tags || []), tagInput.trim()]
      }));
      setTagInput("");
    }
  };

  const removeTag = (tag: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags?.filter(t => t !== tag) || []
    }));
  };

  const addNode = (type: WorkflowNode["type"]) => {
    const newNode: WorkflowNode = {
      id: `node-${Date.now()}`,
      type,
      position: { x: 100 + (formData.nodes?.length || 0) * 200, y: 100 },
      data: {
        label: `${type.charAt(0).toUpperCase() + type.slice(1)} Node`
      }
    };
    setFormData(prev => ({
      ...prev,
      nodes: [...(prev.nodes || []), newNode]
    }));
  };

  const removeNode = (nodeId: string) => {
    setFormData(prev => ({
      ...prev,
      nodes: prev.nodes?.filter(n => n.id !== nodeId) || [],
      edges: prev.edges?.filter(e => e.source !== nodeId && e.target !== nodeId) || []
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitCompare className="h-5 w-5 text-orange-500" />
            {workflow ? "Edit Workflow" : "Create Workflow"}
          </DialogTitle>
          <DialogDescription>
            Build automated flows connecting prompts and agents
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1 pr-4">
          <div className="space-y-6 py-4">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Workflow Name</Label>
                <Input
                  id="name"
                  value={formData.name || ""}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Research & Summarize Pipeline"
                  data-testid="input-workflow-name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description || ""}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe what this workflow does..."
                  className="resize-none"
                  rows={2}
                  data-testid="input-workflow-description"
                />
              </div>
            </div>

            <div className="space-y-3">
              <Label>Trigger Type</Label>
              <div className="grid grid-cols-2 gap-2">
                {triggerTypes.map((trigger) => (
                  <Card 
                    key={trigger.value}
                    className={`cursor-pointer transition-all hover-elevate ${
                      formData.trigger?.type === trigger.value ? "border-primary bg-primary/5" : ""
                    }`}
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      trigger: { type: trigger.value, config: {} }
                    }))}
                    data-testid={`card-trigger-${trigger.value}`}
                  >
                    <CardContent className="p-3 flex items-center gap-3">
                      <trigger.icon className="h-5 w-5 text-muted-foreground" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm">{trigger.label}</p>
                        <p className="text-xs text-muted-foreground">{trigger.description}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {formData.trigger?.type === "scheduled" && (
              <div className="space-y-2">
                <Label htmlFor="schedule">Schedule (Cron Expression)</Label>
                <Input
                  id="schedule"
                  value={formData.trigger?.config?.schedule || ""}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    trigger: {
                      ...prev.trigger!,
                      config: { ...prev.trigger?.config, schedule: e.target.value }
                    }
                  }))}
                  placeholder="0 9 * * * (every day at 9am)"
                  data-testid="input-workflow-schedule"
                />
              </div>
            )}

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Workflow Nodes</Label>
                <Select onValueChange={(value) => addNode(value as WorkflowNode["type"])}>
                  <SelectTrigger className="w-[140px]">
                    <Plus className="h-4 w-4 mr-1" />
                    <SelectValue placeholder="Add Node" />
                  </SelectTrigger>
                  <SelectContent>
                    {nodeTypes.map((node) => (
                      <SelectItem key={node.value} value={node.value}>
                        <span className="flex items-center gap-2">
                          <node.icon className="h-4 w-4" />
                          {node.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {formData.nodes && formData.nodes.length > 0 ? (
                <div className="p-4 border rounded-md bg-muted/10 min-h-[200px]">
                  <div className="flex flex-col items-center gap-0">
                    {formData.nodes.map((node, index) => {
                      const nodeType = nodeTypes.find(n => n.value === node.type);
                      const NodeIcon = nodeType?.icon || Brain;
                      const nodeColors: Record<string, string> = {
                        trigger: "bg-green-500",
                        prompt: "bg-blue-500",
                        agent: "bg-purple-500",
                        condition: "bg-yellow-500",
                        transform: "bg-cyan-500",
                        output: "bg-orange-500",
                        loop: "bg-pink-500",
                        "http-request": "bg-indigo-500",
                        "human-review": "bg-red-500",
                        delay: "bg-gray-500"
                      };
                      return (
                        <div key={node.id} className="flex flex-col items-center">
                          <div 
                            className="flex items-center gap-2 px-4 py-2 border rounded-md bg-card shadow-sm min-w-[180px] group relative"
                            data-testid={`workflow-node-${node.id}`}
                          >
                            <div className={`flex items-center justify-center w-7 h-7 rounded-md ${nodeColors[node.type] || "bg-primary"} text-white`}>
                              <NodeIcon className="h-4 w-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">{node.data.label || nodeType?.label}</p>
                              <p className="text-xs text-muted-foreground capitalize">{node.type}</p>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity absolute -right-2 -top-2 bg-background border shadow-sm"
                              onClick={() => removeNode(node.id)}
                              data-testid={`button-remove-node-${node.id}`}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                          {index < (formData.nodes?.length || 0) - 1 && (
                            <div className="flex flex-col items-center py-1">
                              <div className="w-0.5 h-4 bg-border"></div>
                              <ArrowRight className="h-4 w-4 text-muted-foreground rotate-90" />
                              <div className="w-0.5 h-4 bg-border"></div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 border rounded-md border-dashed bg-muted/10">
                  <GitCompare className="h-10 w-10 mx-auto mb-3 text-muted-foreground opacity-40" />
                  <p className="text-sm font-medium text-muted-foreground mb-1">
                    No nodes yet
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Add prompts, agents, and conditions to build your flow
                  </p>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>Tags</Label>
              <div className="flex gap-2">
                <Input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addTag())}
                  placeholder="Add a tag..."
                  className="flex-1"
                  data-testid="input-workflow-tag"
                />
                <Button variant="outline" onClick={addTag} data-testid="button-add-tag">
                  Add
                </Button>
              </div>
              {formData.tags && formData.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {formData.tags.map((tag) => (
                    <Badge key={tag} variant="secondary" className="gap-1">
                      {tag}
                      <X
                        className="h-3 w-3 cursor-pointer"
                        onClick={() => removeTag(tag)}
                      />
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>
        </ScrollArea>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <div className="flex items-center justify-between w-full">
            {workflow && workflow.createdBy === 'user' && onDelete && (
              <Button 
                variant="destructive" 
                onClick={() => {
                  if (confirm(`Are you sure you want to delete "${workflow.name}"? This action cannot be undone.`)) {
                    onDelete(workflow.id);
                    onOpenChange(false);
                  }
                }}
                data-testid="button-delete-workflow"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </Button>
            )}
            <div className="flex gap-2 ml-auto">
              <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="button-cancel-workflow">
                Cancel
              </Button>
              <Button 
                onClick={handleSave} 
                disabled={!formData.name?.trim()}
                data-testid="button-save-workflow"
              >
                {workflow ? "Update Workflow" : "Create Workflow"}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
